import axios from 'axios';
import { toast } from 'sonner';

// Attach employee auth token to every outgoing request.
// Customer-portal token is attached only if no employee token is present.
axios.interceptors.request.use((config) => {
  try {
    const employeeToken = localStorage.getItem('employee_token');
    const customerToken = localStorage.getItem('customer_portal_token');
    const hasAuthHeader =
      config.headers &&
      (config.headers.Authorization || config.headers.authorization);

    if (!hasAuthHeader) {
      const token = employeeToken || customerToken;
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  } catch (_) {
    // localStorage unavailable (SSR/private mode) — let the request proceed unauthenticated
  }
  return config;
});

// On 401 (expired/revoked session) we used to immediately clear auth and hard
// redirect to login. For a *save* (POST/PUT/PATCH/DELETE) that silently threw
// away everything the user had just typed. Now we distinguish:
//   - Read requests (GET/HEAD): clear auth and redirect, as before, so stale
//     data doesn't linger.
//   - Write requests: do NOT redirect or wipe the form. Warn the user so they
//     can sign back in (in another tab) and re-submit — their typed data stays
//     on screen instead of vanishing.
let redirecting = false;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const config = error?.config || {};
    const url = config.url || '';
    const method = (config.method || 'get').toLowerCase();
    const isLoginRequest = url.includes('/employee/login') || url.includes('/auth/login');
    const isWrite = ['post', 'put', 'patch', 'delete'].includes(method);
    // Customer-portal pages use their own token (localStorage 'access_token')
    // and must bounce to the PORTAL login — not the staff login screen.
    const isPortalContext = window.location.pathname.includes('customer-portal');

    if (status === 401 && !isLoginRequest) {
      if (isWrite) {
        // Don't destroy the user's unsaved work. Just tell them what happened.
        try {
          toast.error('Your session expired, so this was NOT saved. Please sign in again (your entries are still on screen), then save once more.');
        } catch (_) {}
      } else if (!redirecting) {
        redirecting = true;
        try {
          if (isPortalContext) {
            localStorage.removeItem('access_token');
          } else {
            localStorage.removeItem('employee_token');
            localStorage.removeItem('employee_data');
          }
        } catch (_) {}
        const onLogin = window.location.pathname.includes('login');
        if (!onLogin) {
          window.location.href = isPortalContext ? '/customer-portal-login' : '/employee-login';
        }
        setTimeout(() => { redirecting = false; }, 1000);
      }
    }
    return Promise.reject(error);
  }
);

export default axios;
